import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

// Shape returned by GET /students — one row per student_session, joined
// with sp_students + sp_users server-side (see StudentController.GetStudents).
// `id` is the *student* identity id (student_id, a Mongo ObjectID) — not
// the student_session row id — consistent with StudentPDFDownloader.tsx.
interface StudentData {
  id: string;
  user_id: string;
  name: string;
  email_id: string;
  class_id: string;
  class_name: string;
  section: string | null;
  roll_no: number;
  father_name: string;
  father_no: string;
  profile_picture_url: string;
  // NOT currently projected by GetStudents — see backend note in
  // fetchStudents() below. Optional until the backend adds it; the
  // credentials card only renders the "User:" row when this is truthy.
  user_login?: string;
}

// Raw row shape coming back from the Go API's aggregation pipeline
// (student_sessions → sp_students → sp_users). Only the fields this
// component actually consumes are typed here.
interface RawStudentSessionRow {
  student_id: string;
  user_id: string;
  name: string;
  email_id: string;
  class_id: string;
  section: string | null;
  roll_no: number;
  father_name: string;
  father_no: string;
  profile_picture?: { url?: string } | null;
  user_login?: string;
}

interface StudentsListResponse {
  status: string;
  message?: string;
  data: RawStudentSessionRow[];
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
  };
}

const PAGE_LIMIT = 100; // matches the API's max `limit`

const StudentCredentialsGenerator: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Same source of truth StudentsListDashboard.tsx uses. GetStudents is
  // session-scoped server-side, so this MUST be sent — class_id alone
  // does not disambiguate which session's roll you're pulling once a
  // school has more than one session on record.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [password, setPassword] = useState<string>('');
  const [websiteLink, setWebsiteLink] = useState<string>('https://play.google.com/store/apps/details?id=com.skoolpillar.app&hl=en');

  useEffect(() => {
    if (selectedClass && shouldFetchStudents()) {
      fetchStudents();
    } else {
      setStudents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, currentSessionId]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => setClasses(loadedClasses);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classes.find((c) => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const shouldFetchStudents = (): boolean => {
    if (!selectedClass) return false;
    return selectedClassHasSections() ? selectedSection !== null : true;
  };

  // GET /students — see StudentController.GetStudents (Go/Gin backend).
  // Same endpoint/params/pagination contract as StudentPDFDownloader.tsx:
  //   session_id required (from useSchoolDetailsStore.current_session),
  //   class_id required, section only when the class has sections and one
  //   is selected, status "active", paged in PAGE_LIMIT chunks until every
  //   row is collected. school_id omitted — resolved from the caller's JWT.
  //
  // BACKEND GAP: GetStudents' $addFields stage does not currently project
  // a login/username field. Until a login field (e.g.
  // "user_login": "$user_info.user_login" — confirm actual field name on
  // sp_users) is added server-side, row.user_login here will be undefined
  // and the "User:" row on each credential card is skipped rather than
  // showing "undefined".
  const fetchStudents = async (): Promise<void> => {
    if (!currentSessionId) {
      // School details (and its current_session) haven't loaded yet —
      // nothing to fetch until that's available.
      setStudents([]);
      return;
    }
    if (!selectedClass || !shouldFetchStudents()) {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      const className = classes.find((c) => c.id === selectedClass)?.class_name || '';
      const collected: StudentData[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const params: Record<string, string | number> = {
          session_id: currentSessionId,
          class_id: selectedClass,
          status: 'active',
          page,
          limit: PAGE_LIMIT,
        };
        if (selectedClassHasSections() && selectedSection) {
          params.section = selectedSection;
        }

        const response = await apiClient.get<StudentsListResponse>('/students', { params });
        const data = response.data;

        if (data.status !== 'success') {
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: data.message || 'Failed to fetch students', life: 4000
          });
          break;
        }

        const mapped: StudentData[] = (data.data || []).map((row) => ({
          id: row.student_id,
          user_id: row.user_id,
          name: row.name,
          email_id: row.email_id,
          class_id: row.class_id,
          section: row.section,
          roll_no: row.roll_no,
          father_name: row.father_name,
          father_no: row.father_no,
          profile_picture_url: row.profile_picture?.url || '',
          user_login: row.user_login,
          class_name: className,
        }));

        collected.push(...mapped);
        totalPages = data.pagination?.total_pages ?? 1;
        page += 1;
      } while (page <= totalPages);

      setStudents(collected);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching students', life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId: string | null) => setSelectedClass(classId);
  const handleSectionChange = (section: string | null) => setSelectedSection(section);

  const generateCredentialsPDF = () => {
    if (students.length === 0) {
      toast.current?.show({
        severity: 'warn', summary: 'No Students',
        detail: 'No students available to generate credentials', life: 4000
      });
      return;
    }

    const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
    const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
    const title = `Student Credentials - Class ${className}${sectionText}`;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            @page { size: A4; margin: 0.3in; }
            @media print {
              body { margin: 0; font-family: Arial, sans-serif; color: #1e40af; background: white; }
              .no-print { display: none !important; }
              .page-break { page-break-before: always; }
            }

            body { font-family: Arial, sans-serif; margin: 8px; background: white; color: #1e40af; font-size: 12px; line-height: 1.3; }
            :root { --gap: 8px; }
            .controls { margin-bottom: 10px; text-align: center; }
            .controls button { background: #1e40af; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; margin: 0 5px; }
            .controls button.close-btn { background: #dc2626; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1e40af; padding-bottom: 8px; }
            .header h1 { color: #1e40af; margin: 0; font-size: 18px; font-weight: bold; }
            .header .meta { color: #666; font-size: 11px; margin: 2px 0; }

            .page { margin-bottom: 20px; position: relative; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(4, 1fr); gap: var(--gap); height: 1000px; }

            .card {
              padding: 10px;
              border: 2px solid #1e40af;
              border-radius: 6px;
              background: white;
              height: 230px;
              position: relative;
              overflow: hidden;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
            }
            .card-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 5px;
              flex-shrink: 0;
            }
            .profile-section {
              display: flex;
              align-items: center;
              flex: 1;
              min-width: 0;
              overflow: hidden;
            }
            .profile-pic, .profile-placeholder {
              width: 52px;
              height: 52px;
              min-width: 52px;
              border: 2px solid #1e40af;
              border-radius: 6px;
              margin-right: 7px;
            }
            .profile-pic { object-fit: cover; }
            .profile-placeholder {
              background: #f8fafc;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 22px;
              font-weight: bold;
              color: #1e40af;
            }
            .student-name {
              font-size: 13px;
              font-weight: bold;
              color: #1e40af;
              margin: 0;
              line-height: 1.3;
              word-break: break-word;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            .qr-code {
              width: 52px;
              height: 52px;
              min-width: 52px;
              border: 2px solid #1e40af;
              border-radius: 4px;
            }
            .details {
              font-size: 11px;
              flex: 1;
              overflow: hidden;
            }
            .details div {
              margin: 2px 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .details label {
              font-weight: 600;
              width: 52px;
              display: inline-block;
              color: #1e40af;
              flex-shrink: 0;
            }
            .details span { color: #1f2937; }
            .credentials {
              border: 1px solid #1e40af;
              padding: 2px 6px;
              border-radius: 4px;
              background: #f8fafc;
              font-weight: 600;
              display: inline-block;
              margin: 1px 0;
              font-size: 11px;
              max-width: calc(100% - 60px);
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              vertical-align: middle;
            }

            .footer { text-align: center; color: #666; font-size: 10px; margin-top: 15px; border-top: 1px solid #1e40af; padding-top: 8px; }
          </style>
        </head>
        <body>
          <div class="no-print controls">
            <button onclick="window.print()">🖨️ Print / Save as PDF</button>
            <button class="close-btn" onclick="window.close()">❌ Close</button>
          </div>

          <div class="header">
            <h1>SKOOLPILLAR MANAGEMENT SYSTEM</h1>
            <div class="meta">${title}</div>
            <div class="meta">Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}</div>
          </div>

          ${(() => {
            let content = '';
            const cardsPerPage = 12;
            const totalPages = Math.ceil(students.length / cardsPerPage);

            content += `<div class="page-break"></div>`;

            for (let page = 0; page < totalPages; page++) {
              const startIndex = page * cardsPerPage;
              const endIndex = Math.min(startIndex + cardsPerPage, students.length);
              const pageStudents = students.slice(startIndex, endIndex);

              if (page > 0) {
                content += `<div class="page-break"></div>`;
              }

              content += `<div class="page">`;
              content += `<div class="grid">`;

              pageStudents.forEach((student) => {
                content += `
                  <div class="card">
                    <div class="card-header">
                      <div class="profile-section">
                        ${student.profile_picture_url
                          ? `<img src="${student.profile_picture_url}" alt="${student.name}" class="profile-pic" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                             <div class="profile-placeholder" style="display: none;">${student.name.charAt(0)}</div>`
                          : `<div class="profile-placeholder">${student.name.charAt(0)}</div>`
                        }
                        <div class="student-name">${student.name || 'N/A'}</div>
                      </div>
                      ${websiteLink ? `<img src="https://api.qrserver.com/v1/create-qr-code/?size=52x52&data=${encodeURIComponent(websiteLink)}" class="qr-code" alt="QR" />` : ''}
                    </div>
                    <div class="details">
                      ${student.user_login ? `<div><label>User:</label><span class="credentials">${student.user_login}</span></div>` : ''}
                      ${password ? `<div><label>Pass:</label><span class="credentials">${password}</span></div>` : ''}
                      <div><label>Email:</label><span>${student.email_id || 'N/A'}</span></div>
                      <div><label>Class:</label><span>${student.class_name}${student.section ? ` - ${student.section}` : ''}</span></div>
                      <div><label>Roll:</label><span>${student.roll_no || 'N/A'}</span></div>
                      <div><label>Father:</label><span>${student.father_name || 'N/A'}</span></div>
                      <div><label>Phone:</label><span>${student.father_no || 'N/A'}</span></div>
                    </div>
                  </div>
                `;
              });

              const remainingSlots = cardsPerPage - pageStudents.length;
              for (let i = 0; i < remainingSlots; i++) {
                content += `<div class="card" style="border: 1px dashed #ccc; background: #f9f9f9;"></div>`;
              }

              content += `</div></div>`;
            }

            return content;
          })()}

          <div class="footer">
            <p><strong>Skoolpillar Management System</strong> | ${students.length} credentials generated</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);

      toast.current?.show({
        severity: 'success', summary: 'Success',
        detail: `Generated credentials for ${students.length} students`, life: 4000
      });
    } else {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: 'Unable to open print window. Please check your browser settings.', life: 5000
      });
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <GlobalHeaderTitle
          title="Student Credentials Generator"
          subtitle="Generate printable student credentials with login details (3×4 grid)"
          icon="pi pi-id-card"
          buttons={[
            {
              label: "Generate PDF",
              icon: "pi pi-file-pdf",
              onClick: generateCredentialsPDF,
              disabled: students.length === 0 || loading,
              style: {
                background: students.length > 0 && !loading
                  ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                  : undefined
              }
            }
          ]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
        />

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <label style={{
                display: 'block', fontSize: '13px', fontWeight: '600',
                color: '#1e40af', marginBottom: '8px', letterSpacing: '0.02em'
              }}>
                Default Password
              </label>
              <InputText
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter default password"
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block', fontSize: '13px', fontWeight: '600',
                color: '#1e40af', marginBottom: '8px', letterSpacing: '0.02em'
              }}>
                Website Link (QR Code)
              </label>
              <InputText
                value={websiteLink}
                onChange={(e) => setWebsiteLink(e.target.value)}
                placeholder="https://school.example.com"
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px'
                }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div style={{
            background: 'white', borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%'
          }}>
            {students.length > 0 ? (
              <>
                <div style={{
                  padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="pi pi-users" style={{ fontSize: '14px', color: '#059669' }}></i>
                    </div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                      {students.length} student{students.length !== 1 ? 's' : ''} ready for credentials
                    </span>
                  </div>
                  <div style={{
                    padding: '6px 12px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
                    border: '1px solid rgba(5, 150, 105, 0.15)'
                  }}>
                    <span style={{ fontSize: '13px', color: '#059669', fontWeight: '600' }}>
                      {Math.ceil(students.length / 12)} page{Math.ceil(students.length / 12) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div style={{
                  padding: '20px', display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '16px', maxHeight: '600px', overflowY: 'auto'
                }}>
                  {students.map((student) => (
                    <div key={student.id} style={{
                      border: '1.5px solid #e5e7eb', borderRadius: '12px',
                      padding: '16px', background: '#fafbfc', transition: 'all 0.2s ease',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        {student.profile_picture_url ? (
                          <img
                            src={student.profile_picture_url}
                            alt={student.name}
                            style={{
                              width: '48px', height: '48px', borderRadius: '10px',
                              objectFit: 'cover', border: '2px solid #1e40af'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '48px', height: '48px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '18px', fontWeight: '600', color: 'white'
                          }}>
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: '600', fontSize: '16px', color: '#1f2937',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}>
                            {student.name}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            Roll: {student.roll_no}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                        <div><strong>Class:</strong> {student.class_name}{student.section ? ` - ${student.section}` : ''}</div>
                        <div><strong>Father:</strong> {student.father_name}</div>
                        <div><strong>Contact:</strong> {student.father_no}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '60px 20px', minHeight: '400px'
              }}>
                <div style={{
                  width: '56px', height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px', boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                }}>
                  <i
                    className={
                      !currentSessionId
                        ? "pi pi-exclamation-circle"
                        : !selectedClass
                        ? "pi pi-filter"
                        : selectedClassHasSections() && !selectedSection
                        ? "pi pi-list"
                        : "pi pi-inbox"
                    }
                    style={{ fontSize: '25px', color: 'white' }}
                  ></i>
                </div>
                <h3 style={{
                  margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: '700',
                  marginBottom: '8px', textAlign: 'center'
                }}>
                  {!currentSessionId
                    ? "No Active Session"
                    : !selectedClass
                    ? "No Class Selected"
                    : selectedClassHasSections() && !selectedSection
                    ? "No Section Selected"
                    : "No Students Found"
                  }
                </h3>
                <p style={{
                  margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500',
                  textAlign: 'center', maxWidth: '400px', lineHeight: 1.6
                }}>
                  {!currentSessionId
                    ? "Could not determine the current session for this school. Please refresh and try again."
                    : !selectedClass
                    ? "Please select a class from the filter above to load students"
                    : selectedClassHasSections() && !selectedSection
                    ? "Please select a section to load students for this class"
                    : "There are no students enrolled in this class/section yet"
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default StudentCredentialsGenerator;